// src/hooks/useGateAccess.js
import { useEffect, useMemo, useState } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { Connection, LAMPORTS_PER_SOL, PublicKey } from '@solana/web3.js';
import { TOKEN_2022_PROGRAM_ID, TOKEN_PROGRAM_ID } from '@solana/spl-token';
import { createUmi } from '@metaplex-foundation/umi-bundle-defaults';
import {
    mplTokenMetadata,
    fetchAllDigitalAssetByOwner,
} from '@metaplex-foundation/mpl-token-metadata';
import { fetchAssetsByOwner } from '@metaplex-foundation/mpl-core';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { clearPasswordGateAccess, getPasswordGateAccessSource, hasPasswordGateAccess } from '../utils/passwordGateAccess';
import { DONATION_TOKENS } from '../utils/donationTokens';
import { isVerifiedPayment, paymentCreditAtLeast } from '../utils/activityEvents';
import { getPaymentNetwork } from '../utils/paymentNetwork';
import { readDonationRecords, donationCreditAtLeast } from '../utils/donationRecords';
import { getGateAccessReason } from '../utils/gatePresentation';

// --- in-flight dedupe (NOT cached results) ---
const inflightGateList = new Map();   // listId -> Promise<data|null>
const inflightDasByOwner = new Map(); // key -> Promise<{mintSet, collectionSet}>
const inflightOnchainByOwner = new Map(); // key -> Promise<{mintSet, collectionSet}>
const inflightTokensHeldByOwner = new Map(); // key -> Promise<number>

/** Extract cNFT collection IDs from a DAS item (same logic you used before). */
function extractCnftCollectionIds(item) {
    const ids = new Set();
    if (Array.isArray(item?.grouping)) {
        for (const g of item.grouping) {
            if (g?.group_key === 'collection' && g?.group_value) ids.add(String(g.group_value));
        }
    }
    const keyA = item?.collection?.key; if (keyA) ids.add(String(keyA));
    const keyB = item?.content?.metadata?.collection?.key; if (keyB) ids.add(String(keyB));
    const keyC = item?.data?.collection?.key; if (keyC) ids.add(String(keyC));
    return Array.from(ids);
}

/** Raw DAS fallback (object params †’ array-of-one), same as your working version. */
async function rawDasGetAssetsByOwner(ownerAddress, rpcUrl, { page = 1, limit = 1000 } = {}) {
    if (!rpcUrl) return [];
    const headers = { 'content-type': 'application/json' };
    const call = async (params) => {
        const res = await fetch(rpcUrl, {
            method: 'POST',
            headers,
            body: JSON.stringify({
                jsonrpc: '2.0',
                id: 'getAssetsByOwner',
                method: 'getAssetsByOwner',
                params,
            }),
        });
        const json = await res.json();
        if (json?.error) throw new Error(json.error?.message || 'DAS error');
        return json?.result?.items ?? [];
    };
    let lastError;
    try {
        const items = await call({ ownerAddress, page, limit });
        if (Array.isArray(items)) return items;
    } catch (error) { lastError = error; }
    try {
        const items = await call([{ ownerAddress, page, limit }]);
        if (Array.isArray(items)) return items;
    } catch (error) { lastError = error; }
    if (lastError) throw lastError;
    return [];
}


async function getGateListOnce(listId) {
    if (!listId) return null;
    if (inflightGateList.has(listId)) return inflightGateList.get(listId);

    const p = (async () => {
        const snap = await getDoc(doc(db, 'gateLists', listId));
        return snap.exists() ? snap.data() : null;
    })().finally(() => {
        inflightGateList.delete(listId);
    });

    inflightGateList.set(listId, p);
    return p;
}

function extractMintIdFromDasItem(it) {
    return it?.id ? String(it.id) : null;
}

function parseScheduleDateMs(value) {
    const raw = String(value ?? '').trim();
    if (!raw) return null;
    const ms = Date.parse(raw);
    return Number.isFinite(ms) ? ms : null;
}

function hasScheduledGateAccess(gate, nowMs = Date.now()) {
    const startMs = parseScheduleDateMs(gate?.startAt);
    if (startMs == null) return false;
    if (nowMs < startMs) return false;

    const endRaw = String(gate?.endAt ?? '').trim();
    if (!endRaw) return true;

    const endMs = parseScheduleDateMs(endRaw);
    if (endMs == null) return false;
    if (endMs < startMs) return false;

    return nowMs <= endMs;
}

function resolveHeldTokenConfig({ currency, tokenMint }) {
    const symbol = String(currency || '').trim();
    const mint = String(tokenMint || '').trim();
    return (DONATION_TOKENS || []).find((token) => (
        (symbol && token.symbol === symbol) ||
        (mint && token.mint === mint)
    )) || {
        symbol: symbol || 'SOL',
        mint: mint || null,
        isNative: symbol === 'SOL' || !mint,
    };
}

async function getTokensHeldBalanceOnce({ owner, endpoint, currency, tokenMint }) {
    const cfg = resolveHeldTokenConfig({ currency, tokenMint });
    const symbol = cfg?.symbol || currency || 'SOL';
    const mint = tokenMint || cfg?.mint || '';
    const isNative = cfg?.isNative || symbol === 'SOL';
    const key = `${endpoint || ''}|${owner}|${symbol}|${mint || 'native'}`;
    if (inflightTokensHeldByOwner.has(key)) return inflightTokensHeldByOwner.get(key);

    const p = (async () => {
        if (!endpoint || !owner) return 0;
        const connection = new Connection(endpoint, 'confirmed');
        const ownerPubkey = new PublicKey(owner);

        if (isNative) {
            const lamports = await connection.getBalance(ownerPubkey, 'confirmed');
            return lamports / LAMPORTS_PER_SOL;
        }

        if (!mint) return 0;
        const mintPubkey = new PublicKey(mint);
        const programs = [TOKEN_PROGRAM_ID, TOKEN_2022_PROGRAM_ID];
        let total = 0;

        for (const programId of programs) {
            const accounts = await connection.getParsedTokenAccountsByOwner(
                ownerPubkey,
                { programId },
                'confirmed'
            );
            for (const acc of accounts.value || []) {
                const info = acc.account?.data?.parsed?.info;
                if (info?.mint !== mintPubkey.toBase58()) continue;
                const tokenAmount = info?.tokenAmount || {};
                const amount =
                    tokenAmount.uiAmount != null
                        ? Number(tokenAmount.uiAmount)
                        : Number(tokenAmount.uiAmountString || 0);
                if (Number.isFinite(amount)) total += amount;
            }
        }

        return total;
    })().finally(() => {
        inflightTokensHeldByOwner.delete(key);
    });

    inflightTokensHeldByOwner.set(key, p);
    return p;
}


async function getInventoryViaDasOnce({ owner, dasRpc }) {
    const key = `${dasRpc || ''}|${owner}`;
    if (inflightDasByOwner.has(key)) return inflightDasByOwner.get(key);

    const p = (async () => {
        const mintSet = new Set();
        const collectionSet = new Set();
        const limit = 1000;

        // DAS pages can be full even when total only reports this page's count.
        for (let page = 1; ; page += 1) {
            const items = await rawDasGetAssetsByOwner(owner, dasRpc, { page, limit });
            for (const it of items) {
                const mint = extractMintIdFromDasItem(it);
                if (mint) mintSet.add(mint);

                for (const cid of extractCnftCollectionIds(it)) {
                    collectionSet.add(String(cid));
                }
            }
            if (items.length < limit) break;
        }

        return { mintSet, collectionSet };
    })().finally(() => {
        inflightDasByOwner.delete(key);
    });

    inflightDasByOwner.set(key, p);
    return p;
}

async function getInventoryViaOnchainOnce({ owner, endpoint }) {
    const key = `${endpoint || ''}|${owner}`;
    if (inflightOnchainByOwner.has(key)) return inflightOnchainByOwner.get(key);

    const p = (async () => {
        const umi = createUmi(endpoint).use(mplTokenMetadata());

        const [legacy, core] = await Promise.all([
            fetchAllDigitalAssetByOwner(umi, owner),
            fetchAssetsByOwner(umi, owner, { skipDerivePlugins: false }),
        ]);

        const extractCoreCollectionId = (asset) => {
            const top = asset?.collection?.publicKey || asset?.collection?.key || asset?.collectionAddress || (asset?.updateAuthority?.type === 'Collection' ? asset.updateAuthority.address : null);
            const plugin = asset?.plugins?.find?.(p2 => p2?.__kind === 'Collection' || p2?.type === 'collection');
            const pluginId = plugin?.collection?.publicKey || plugin?.collection?.key || plugin?.collectionAddress || null;
            return top || pluginId || null;
        };

        const mintSet = new Set();
        const collectionSet = new Set();

        for (const a of legacy) {
            if (a?.publicKey) mintSet.add(String(a.publicKey));
            const ck = a?.metadata?.collection?.value?.key;
            if (ck) collectionSet.add(String(ck));
        }

        for (const a of core) {
            if (a?.publicKey) mintSet.add(String(a.publicKey));
            const ck = extractCoreCollectionId(a);
            if (ck) collectionSet.add(String(ck));
        }

        return { mintSet, collectionSet };
    })().finally(() => {
        inflightOnchainByOwner.delete(key);
    });

    inflightOnchainByOwner.set(key, p);
    return p;
}


export function useGateAccess(input, opts = {}) {
    const { publicKey } = useWallet();
    const componentId = String(opts?.componentId ?? '').trim();
    const spaceOwnerUid = String(opts?.spaceOwnerUid || '').trim();
    const [hasAccess, setHasAccess] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [gateName, setGateName] = useState('');
    const [gateNames, setGateNames] = useState([]);
    const [accessReason, setAccessReason] = useState('');
    const [scheduleTick, setScheduleTick] = useState(0);
    const [mintRefreshTick, setMintRefreshTick] = useState(0);
    useEffect(() => {
        const refresh = () => setMintRefreshTick(value => value + 1);
        window.addEventListener('muulti-publishing-confirmed', refresh);
        window.addEventListener('muulti-payment-confirmed', refresh);
        return () => {
            window.removeEventListener('muulti-publishing-confirmed', refresh);
            window.removeEventListener('muulti-payment-confirmed', refresh);
        };
    }, []);
    const [evaluatedKey, setEvaluatedKey] = useState('');
    const [passwordResetTick, setPasswordResetTick] = useState(0);
    const [matchedPasswordGate, setMatchedPasswordGate] = useState(null);
    const [passwordResetError, setPasswordResetError] = useState(null);

    // NEW: normalize donationLookup (Map or plain object)
    const donationLookup = useMemo(() => {
        const src = input?.donationLookup;
        if (!src) return null;
        if (src instanceof Map) return src;
        if (typeof src === 'object') {
            const m = new Map();
            for (const [k, v] of Object.entries(src)) m.set(String(k), v || {});
            return m;
        }
        return null;
    }, [input?.donationLookup]);


    function labelForGate(g) {
        switch (g?.type) {
            case 'public': return 'Public';
            case 'password': return 'password';
            case 'scheduledAccess': {
                const scheduleName = String(g?.scheduleName || '').trim();
                return scheduleName || 'Scheduled Access';
            }
            case 'token': return 'Tokens Required';
            case 'collection': return 'Collection Required';
            case 'compressedCollection': return 'Compressed Collection Required';
            case 'tokensHeld': {
                const threshold = typeof g.tokenThreshold === 'number' ? g.tokenThreshold : null;
                const currency = g?.currency || 'SOL';
                return threshold != null
                    ? `Tokens Held Required — Must hold at least ${threshold} ${currency}`
                    : `Tokens Held Required — Must hold ${currency}`;
            }
            case 'list': return g.listId ? `List ${g.listId}` : 'List';
            case 'donation': {
                const th = typeof g.donationThreshold === 'number' ? g.donationThreshold : null;
                const name = g?.campaignName || null;
                const currency = g?.currency || null;
                if (name && th != null) return currency
                    ? `Donation Required — Must donate at least ${th} ${currency} to ${name}`
                    : `Donation Required — Must donate at least ${th} to ${name}`;
                if (name) return `Donation Required — Must donate to ${name}`;
                if (th != null) return currency
                    ? `Donation Required — Must donate at least ${th} ${currency}`
                    : `Donation Required — Must donate at least ${th}`;
                return 'Donation Required — Must donate to unlock';
            }
            case 'directPayment': {
                const p = typeof g.priceSol === 'number' ? g.priceSol : null;
                const currency = g?.currency || 'SOL';
                return p != null ? `Payment Required — Must pay at least ${p} ${currency}` : 'Payment Required';
            }
            default: return 'Gate';
        }
    }

    function labelForGateWithCfg(g, cfgById, donationLookup) {
        if (!g) return 'Gate';
        if (g.type === 'public') return 'Public';
        if (g.type === 'password') return 'password';
        if (g.type === 'scheduledAccess') {
            const scheduleName = String(g?.scheduleName || '').trim();
            return scheduleName || 'Scheduled Access';
        }
        if (g.type === 'tokensHeld') {
            const threshold = typeof g.tokenThreshold === 'number' ? g.tokenThreshold : null;
            const currency = g?.currency || 'SOL';
            return threshold != null
                ? `Tokens Held Required — Must hold at least ${threshold} ${currency}`
                : `Tokens Held Required — Must hold ${currency}`;
        }

        if (g.type === 'donation') {
            const compId = String(g.donationCompId || '');
            const info = (compId && donationLookup) ? donationLookup.get(compId) : null;
            const name = info?.campaignName || g?.campaignName || null;
            const currency = info?.currency || g?.currency || null;
            const th = typeof g.donationThreshold === 'number' ? g.donationThreshold : null;

            if (name && th != null) return currency ? `Donation Required — Must donate at least ${th} ${currency} to ${name}` : `Donation Required — Must donate at least ${th} to ${name}`;
            if (name) return `Donation Required — Must donate to ${name}`;
            if (th != null) return currency ? `Donation Required — Must donate at least ${th} ${currency}` : `Donation Required — Must donate at least ${th}`;
            return 'Donation Required — Must donate to unlock';
        }

        // list-backed types prefer configured gate list name when available
        const id = g.listId || '';
        const cfg = id ? cfgById?.get(id) : null;
        const cfgName = cfg?.name || '';
        if (cfgName) {
            const gateType = cfg?.gateType || g.type;
            const prefix =
                gateType === 'token' ? 'Tokens Required' :
                gateType === 'collection' ? 'Collection Required' :
                gateType === 'compressedCollection' ? 'Compressed Collection Required' :
                'Gate Required';
            return `${prefix} — Must own at least 1 asset from ${cfgName}`;
        }

        // fallback
        if (g.type === 'token') return 'Tokens Required — Must own at least 1 asset from the required collection';
        if (g.type === 'collection') return 'Collection Required — Must own at least 1 asset from the required collection';
        if (g.type === 'compressedCollection') return 'Compressed Collection Required — Must own at least 1 asset from the required collection';
        if (g.type === 'directPayment') {
            const p = typeof g.priceSol === 'number' ? g.priceSol : null;
            const currency = g?.currency || 'SOL';
            return p != null ? `Payment Required — Must pay at least ${p} ${currency}` : 'Payment Required — Must pay to unlock';
        }
        if (g.type === 'token') return `Tokens Required — Must own ${labelForGate(g)}`;
        if (g.type === 'collection') return `Collection Required — Must own ${labelForGate(g)}`;
        if (g.type === 'compressedCollection') return `Compressed Collection Required — Must own ${labelForGate(g)}`;
        return labelForGate(g);
    }

    function joinGateLabels(labels, logic) {
        const list = (labels || []).filter(Boolean);
        if (list.length === 0) return '';
        if (list.length === 1) return list[0];
        const conj = logic === 'all' ? 'and' : 'or';
        if (list.length === 2) return `${list[0]} ${conj} ${list[1]}`;
        const head = list.slice(0, -1).join(', ');
        const tail = list[list.length - 1];
        return `${head}, ${conj} ${tail}`;
    }

    function normalizeGateListForAccess(gates) {
        const list = (Array.isArray(gates) ? gates : []).filter(Boolean);
        const restricted = list.filter((g) => g?.type !== 'public');
        if (restricted.length > 0) return restricted;
        return [{ type: 'public' }];
    }

// Accept:
//  A) { gates: [...], logic }
//  B) plain single gate
//  C) single gate object that also contains additional gates + logic (legacy save shape)
    const normalized = useMemo(() => {
        if (!input) return { gateList: [{ type: 'public' }], logic: 'any' };

        // A) explicit multi gate payload
        if (Array.isArray(input?.gates)) {
            return {
                gateList: normalizeGateListForAccess(input.gates),
                logic: input.logic === 'all' ? 'all' : 'any',
            };
        }

        // C) implicit multi gate payload saved on the "gate" object itself
        const extra =
            (Array.isArray(input?.extraGates) && input.extraGates) ||
            (Array.isArray(input?.additionalGates) && input.additionalGates) ||
            (Array.isArray(input?.moreGates) && input.moreGates) ||
            [];

        const logic =
            (input?.logic === 'all' || input?.gateLogic === 'all') ? 'all' : 'any';

        if (extra.length > 0 || input?.gateLogic || input?.logic) {
            const primary = { ...input };
            delete primary.extraGates;
            delete primary.additionalGates;
            delete primary.moreGates;
            delete primary.gateLogic;
            delete primary.logic;

            const gateList = [primary, ...extra].filter(Boolean);
            return { gateList: normalizeGateListForAccess(gateList), logic };
        }

        // B) plain single gate
        return { gateList: normalizeGateListForAccess([input]), logic: 'any' };
    }, [input]);


    const { gateList, logic } = normalized;
    // console.debug('[gate] normalized', { logic, gateCount: gateList?.length, gateList });

    const depKey = useMemo(() => JSON.stringify({
        componentId,
        spaceOwnerUid,
        logic,
        gates: (gateList || []).map(g => ({
            type: g?.type || 'unknown',
            listId: g?.listId || '',
            donationCompId: g?.donationCompId || '',
            donationThreshold: typeof g?.donationThreshold === 'number' ? g.donationThreshold : null,
            tokenThreshold: typeof g?.tokenThreshold === 'number' ? g.tokenThreshold : null,
            priceSol: typeof g?.priceSol === 'number' ? g.priceSol : null,
            password: typeof g?.password === 'string' ? g.password : '',
            scheduleName: typeof g?.scheduleName === 'string' ? g.scheduleName : '',
            startAt: typeof g?.startAt === 'string' ? g.startAt : '',
            endAt: typeof g?.endAt === 'string' ? g.endAt : '',
            payToUid: g?.payToUid || '',
            targetId: g?.targetId || '',
            currency: g?.currency || '',
            tokenMint: g?.tokenMint || '',

            tokenIds: Array.isArray(g?.tokenIds) ? g.tokenIds.map(String) : [],
            collectionIds: Array.isArray(g?.collectionIds) ? g.collectionIds.map(String) : [],
            compressedCollectionIds: Array.isArray(g?.compressedCollectionIds) ? g.compressedCollectionIds.map(String) : [],
        })),
    }), [gateList, logic, componentId, spaceOwnerUid]);

    // Opaque UI scope: never expose or persist depKey, which includes passwords.
    const viewerAddress = publicKey?.toBase58?.() || '';
    // These dependencies intentionally reset a UI identity without exposing gate data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const revealScope = useMemo(() => ({}), [depKey, viewerAddress, opts?.isEditor, passwordResetTick]);

    const evaluationKey = JSON.stringify({
        depKey,
        owner: publicKey?.toBase58?.() || '',
        isEditor: !!opts?.isEditor,
        refreshKey: opts?.refreshKey ?? 0,
        scheduleTick,
        passwordResetTick,
        mintRefreshTick,
    });

    useEffect(() => {
        const hasScheduledGate = (gateList || []).some((g) => g?.type === 'scheduledAccess');
        if (!hasScheduledGate) return undefined;
        const intervalId = setInterval(() => {
            setScheduleTick((v) => v + 1);
        }, 30000);
        return () => clearInterval(intervalId);
    }, [gateList]);

    useEffect(() => {
        let stop = false;
        let cfgById = new Map();
        const finishAccessCheck = (allowed, matchedGates = []) => {
            if (stop) return;
            setMatchedPasswordGate(allowed ? matchedGates.find((g) => (
                g?.type === 'password' && getPasswordGateAccessSource(g, componentId)
            )) || null : null);
            if (allowed) {
                setAccessReason(getGateAccessReason(matchedGates, logic, cfgById, donationLookup));
                const labels = matchedGates.map((g) => {
                    if (g?.type === 'password') {
                        const source = getPasswordGateAccessSource(g, componentId);
                        return source === 'browser'
                            ? 'password (saved in this browser)'
                            : source === 'session'
                                ? 'password (saved for this session)'
                                : 'password';
                    }
                    return labelForGateWithCfg(g, cfgById, donationLookup);
                });
                setGateNames(labels);
                setGateName(joinGateLabels(labels, logic));
            }
            setHasAccess(allowed);
            setIsLoading(false);
            setEvaluatedKey(evaluationKey);
        };
        (async () => {
            // EDITOR BYPASS (verified editor should always see content)
            if (opts?.isEditor) {
                if (!stop) {
                    setHasAccess(true);
                    setIsLoading(false);
                    setMatchedPasswordGate(null);
                    setGateNames(['Editor']);
                    setGateName('Editor');
                    setEvaluatedKey(evaluationKey);
                }
                return; //no gate checks while editing
            }

            setIsLoading(true);
            setHasAccess(false);

            const labelsQuick = (Array.isArray(gateList) ? gateList : [])
                .map(labelForGate)
                .filter(Boolean);
            setGateNames(labelsQuick);
            setGateName(joinGateLabels(labelsQuick, logic));

            const evalWithoutWallet = (g) => {
                if (!g || g.type === 'public') return true;
                if (g.type === 'password') return hasPasswordGateAccess(g, componentId);
                if (g.type === 'scheduledAccess') return hasScheduledGateAccess(g);
                return false;
            };
            const localGates = gateList.filter((g) => (
                g.type === 'public' || g.type === 'password' || g.type === 'scheduledAccess'
            ));
            const localMatches = localGates.filter(evalWithoutWallet);
            if (logic === 'any' && localMatches.length > 0) {
                finishAccessCheck(true, localMatches.slice(0, 1));
                return;
            }
            if (logic === 'all' && localMatches.length < localGates.length) {
                finishAccessCheck(false);
                return;
            }
            if (localGates.length === gateList.length) {
                finishAccessCheck(logic === 'all', gateList);
                return;
            }

            // Resolve gate list names even without a wallet
            try {
                const listIds = Array.from(new Set(
                    (gateList || [])
                        .filter(g => g && g.listId && (
                            g.type === 'token' ||
                            g.type === 'collection' ||
                            g.type === 'compressedCollection' ||
                            g.type === 'list'
                        ))
                        .map(g => g.listId)
                ));
                if (listIds.length > 0) {
                    const cfgPairs = await Promise.all(listIds.map(async (id) => {
                        const cfg = await getGateListOnce(id);
                        return [id, cfg];
                    }));
                    cfgById = new Map(cfgPairs);
                    const labelsFinal = (gateList || [])
                        .map(g => labelForGateWithCfg(g, cfgById, donationLookup))
                        .filter(Boolean);
                    if (stop) return;
                    setGateNames(labelsFinal);
                    setGateName(joinGateLabels(labelsFinal, logic));
                }
            } catch {
                // keep quick labels if lookup fails
            }

            // ANY + public †’ allow
            const hasPublic = (gateList || []).some(g => !g || g.type === 'public');
            if (logic === 'any' && hasPublic) {
                finishAccessCheck(true, gateList.filter((g) => g.type === 'public'));
                return;
            }

            // Need wallet for non-public gates
            if (!publicKey) {
                if (logic === 'any') {
                    const matchedGate = (gateList || []).find(evalWithoutWallet);
                    finishAccessCheck(!!matchedGate, matchedGate ? [matchedGate] : []);
                    return;
                }

                const okAll = (gateList || []).every((g) => evalWithoutWallet(g));
                finishAccessCheck(okAll, gateList);
                return;
            }

            const owner = publicKey.toBase58();
            const endpoint = process.env.REACT_APP_RPC_ENDPOINT;
            const dasRpc  = process.env.REACT_APP_DAS_RPC || process.env.REACT_APP_RPC_ENDPOINT;
            try {
                // Reuse the list configs already resolved for the gate labels.



// œ… DAS-first inventory (fast, indexed). Not cached: only in-flight deduped.
                const needsInventory = gateList.some(g => ['token', 'collection', 'compressedCollection', 'list'].includes(g?.type));
                const umi = needsInventory ? createUmi(endpoint).use(mplTokenMetadata()) : null;
                let mintSet = new Set(), collectionSet = new Set();
                let usedDas = false;
                if (needsInventory) {
                    if (!endpoint) throw new Error('Missing REACT_APP_RPC_ENDPOINT (endpoint required for NFT gate checks)');
                    try {
                        const inv = await getInventoryViaDasOnce({ owner, dasRpc });
                        mintSet = inv.mintSet;
                        collectionSet = inv.collectionSet;
                        usedDas = true;
                    } catch {
                        const inv = await getInventoryViaOnchainOnce({ owner, endpoint });
                        mintSet = inv.mintSet;
                        collectionSet = inv.collectionSet;
                    }
                }

                // Lazy compressed set (computed on first compressed gate)
                let cnftCollectionSet = null;
                const ensureCompressedSet = async () => {
                    if (cnftCollectionSet) return cnftCollectionSet;

                    // UMI DAS (object params)
                    try {
                        const pageObj = await umi.rpc.getAssetsByOwner({ ownerAddress: owner, limit: 1000, page: 1 });
                        if (Array.isArray(pageObj?.items)) {
                            const set = new Set();
                            for (const it of pageObj.items) for (const id of extractCnftCollectionIds(it)) set.add(String(id));
                            cnftCollectionSet = set;
                            // console.debug('[gate] fetched compressed set size =', cnftCollectionSet.size, 'sample =', Array.from(cnftCollectionSet).slice(0, 5));
                            return cnftCollectionSet;
                        }
                    } catch {}

                    // UMI DAS (array-of-one)
                    try {
                        const pageArr = await umi.rpc.getAssetsByOwner([{ ownerAddress: owner, limit: 1000, page: 1 }]);
                        if (Array.isArray(pageArr?.items)) {
                            const set = new Set();
                            for (const it of pageArr.items) for (const id of extractCnftCollectionIds(it)) set.add(String(id));
                            cnftCollectionSet = set;
                            // console.debug('[gate] fetched compressed set size =', cnftCollectionSet.size, 'sample =', Array.from(cnftCollectionSet).slice(0, 5));
                            return cnftCollectionSet;
                        }
                    } catch {}

                    // Raw JSON-RPC (same shape you used before)
                    try {
                        const items = await rawDasGetAssetsByOwner(owner, dasRpc, { page: 1, limit: 1000 });
                        const set = new Set();
                        for (const it of items) for (const id of extractCnftCollectionIds(it)) set.add(String(id));
                        cnftCollectionSet = set;
                        // console.debug('[gate] fetched compressed set size =', cnftCollectionSet.size, 'sample =', Array.from(cnftCollectionSet).slice(0, 5));
                        return cnftCollectionSet;
                    } catch {
                        cnftCollectionSet = new Set(); // treat as empty if not supported
                        console.debug('[gate] fetched compressed set EMPTY (DAS unsupported or empty)');
                        return cnftCollectionSet;
                    }
                };

                const evalGate = async (g) => {
                    // console.debug('[gate] eval', { type: g?.type, listId: g?.listId });
                    if (!g || g.type === 'public') return true;
                    if (g?.type === 'password') return hasPasswordGateAccess(g, componentId);
                    if (g?.type === 'scheduledAccess') return hasScheduledGateAccess(g);

                    // -------------------------------------------------
                    // 1) LIST-BACKED GATES TAKE PRIORITY
                    // -------------------------------------------------
                    if (g?.listId) {
                        const cfg = cfgById.get(g.listId);
                        if (!cfg) return false;

                        const gateType = cfg.gateType;

                        if (gateType === 'token' && Array.isArray(cfg.hashList)) {
                            const targets = cfg.hashList.map(String);
                            return targets.some((m) => mintSet.has(String(m)));
                        }

                        if (gateType === 'collection') {
                            const targets = Array.isArray(cfg.collectionIds)
                                ? cfg.collectionIds.map(s => String(s).trim())
                                : (cfg.collectionId ? [String(cfg.collectionId).trim()] : []);
                            return targets.some((t) => collectionSet.has(String(t)));
                        }

                        if (gateType === 'compressedCollection') {
                            const targets = Array.isArray(cfg.compressedCollectionIds)
                                ? cfg.compressedCollectionIds.map(s => String(s).trim())
                                : Array.isArray(cfg.collectionIds) ? cfg.collectionIds.map(s => String(s).trim())
                                : (cfg.compressedCollectionId || cfg.collectionId ? [String(cfg.compressedCollectionId || cfg.collectionId).trim()] : []);

                            const cnftSet = usedDas ? collectionSet : await ensureCompressedSet();
                            return targets.some((t) => cnftSet.has(String(t)));
                        }

                        return false;
                    }

                    // -------------------------------------------------
                    // 2) DONATION GATE
                    // -------------------------------------------------
                    if (g.type === 'donation') {
                        try {
                            const compId = String(g.donationCompId || '');
                            const threshold = Number(g.donationThreshold || 0);
                            if (!compId || !(threshold > 0)) return false;
                            const info = donationLookup?.get(compId) || {};
                            const donationOwner = spaceOwnerUid;
                            if (!donationOwner) return false;
                            const gateCurrency = info?.currency || g?.currency || null;
                            const gateMint = info?.tokenMint || g?.tokenMint || null;
                            const records = await readDonationRecords({ componentId: compId, spaceOwnerUid: donationOwner,
                                sender: owner, currency: gateCurrency, tokenMint: gateMint }, endpoint);
                            return donationCreditAtLeast(records, threshold);
                        } catch {
                            return false;
                        }
                    }

                // -------------------------------------------------
                // 3) DIRECT PAYMENT GATE
                // -------------------------------------------------
                if (g.type === 'directPayment') {
                    try {
                        const targetId = String(g.targetId || '');
                        const payTo = String(g.payToUid || '');
                        const priceAmount = Number(g.priceSol || 0);
                        const gateCurrency = g?.currency || 'SOL';
                        const gateMint = g?.tokenMint || '';
                        if (!targetId || !payTo || !(priceAmount > 0)) return false;
                        const paymentCluster = await getPaymentNetwork(new Connection(endpoint, 'confirmed'));

                        const qSnap = await getDocs(query(
                            collection(db, 'paymentReceipts'),
                            where('kind', '==', 'directPayment'),
                            where('targetId', '==', targetId),
                            where('payer', '==', owner)
                        ));

                        const matching = [];
                        qSnap.forEach(d => {
                            const data = d.data() || {};
                            if (!isVerifiedPayment(data) || data.cluster !== paymentCluster) return;
                            if (data.payTo !== payTo) return;
                            if (data.status !== 'confirmed') return;
                            const recCurrency = data.currency || 'SOL';
                            const recMint = data.tokenMint || '';
                            const currencyMatch = gateCurrency ? gateCurrency === recCurrency : true;
                            const mintMatch = gateMint ? gateMint === recMint : true;
                            if (!currencyMatch || !mintMatch) return;
                            matching.push(data);
                        });
                        return paymentCreditAtLeast(matching, priceAmount);
                    } catch {
                        return false;
                    }
                }

                // -------------------------------------------------
                // 4) TOKENS HELD GATE
                // -------------------------------------------------
                if (g.type === 'tokensHeld') {
                    try {
                        const threshold = Number(g.tokenThreshold || 0);
                        const currency = g?.currency || 'SOL';
                        const tokenMint = g?.tokenMint || '';
                        if (!(threshold > 0)) return false;
                        const balance = await getTokensHeldBalanceOnce({
                            owner,
                            endpoint,
                            currency,
                            tokenMint,
                        });
                        return balance >= threshold;
                    } catch {
                        return false;
                    }
                }

                // -------------------------------------------------
                // 5) DIRECT (INLINE) GATES
                // -------------------------------------------------
                if (g.type === 'token') {
                    const targets = Array.isArray(g.tokenIds) ? g.tokenIds.map(String) : [];
                    return targets.some((m) => mintSet.has(String(m)));
                }

                    if (g.type === 'collection') {
                        const targets = Array.isArray(g.collectionIds) ? g.collectionIds.map(s => String(s).trim()) : [];
                        return targets.some((t) => collectionSet.has(String(t)));
                    }

                    if (g.type === 'compressedCollection') {
                        const targets = Array.isArray(g.compressedCollectionIds)
                            ? g.compressedCollectionIds.map(s => String(s).trim())
                            : [];

                        const cnftSet = usedDas ? collectionSet : await ensureCompressedSet();
                        return targets.some((t) => cnftSet.has(String(t)));
                    }

                    return false;
                };



                // Combine with logic (sequential to allow lazy compressed load mid-loop)
                if (logic === 'any') {
                    let okAny = false;
                    let matchedGate = null;
                    for (const g of (gateList || [])) {
                        // eslint-disable-next-line no-await-in-loop
                        const ok = await evalGate(g);
                        if (ok) { okAny = true; matchedGate = g; break; }
                    }
                    finishAccessCheck(okAny, matchedGate ? [matchedGate] : []);
                    return;
                } else {
                    let okAll = true;
                    for (const g of (gateList || [])) {
                        // eslint-disable-next-line no-await-in-loop
                        const ok = await evalGate(g);
                        if (!ok) { okAll = false; break; }
                    }
                    finishAccessCheck(okAll, gateList);
                    return;
                }
            } catch (err) {
                console.error('Gate access check error:', err);
                finishAccessCheck(false);
            }
        })();
        return () => { stop = true; };
    }, [publicKey, depKey, componentId, spaceOwnerUid, opts?.isEditor, opts?.refreshKey, scheduleTick, evaluationKey]);

    const isCurrentEvaluation = evaluatedKey === evaluationKey;
    const resetPasswordAccess = hasAccess && isCurrentEvaluation && !isLoading && matchedPasswordGate && !opts?.isEditor
        ? () => {
            const cleared = clearPasswordGateAccess(matchedPasswordGate, componentId);
            setPasswordResetError(cleared ? null : {
                depKey, message: 'Could not clear the saved unlock. Please try again.',
            });
            // Revoke the old result before reevaluating ANY/ALL access.
            setPasswordResetTick((value) => value + 1);
            return cleared;
        }
        : null;
    return [
        hasAccess && isCurrentEvaluation && !isLoading,
        isLoading || !isCurrentEvaluation,
        gateName,
        gateNames,
        {
            // Presentation metadata does not participate in access evaluation.
            requiresReveal: !opts?.isEditor && gateList.some(g => g?.type !== 'public'),
            revealScope,
            accessReason: hasAccess && isCurrentEvaluation && !isLoading ? accessReason : '',
            resetPasswordAccess,
            resetPasswordError: passwordResetError?.depKey === depKey ? passwordResetError.message : '',
        },
    ];
}


